import { Grid, TextField } from "@mui/material";
import { FormikProps, useFormikContext } from "formik";
import { isNil } from "ramda";
import React, { memo } from "react";
import { Data } from "../../../store/merchant/types";
import { Mcc } from "../../../types";
import TextInput from "../../../common/formElements/TextInput";
import SelectDropDown, {
  Option,
} from "../../../common/formElements/SelectDropDown";
import AutocompleteField from "../../../common/formElements/AutocompleteField";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";

const CREATE = "CREATE";
const UPDATE = "UPDATE";

interface EditMerchantInformationComponent {
  merchant?: Data;
  isUpdateAllowed?: boolean;
}

const EditMerchantInformationComponent = (
  props: EditMerchantInformationComponent
) => {
  const { merchant, isUpdateAllowed } = props;
  const formikProps = useFormikContext();
  const { values, touched, errors, setFieldValue } = formikProps as FormikProps<
    Data
  >;
  const mcc = useSelector((state: ApplicationState) => state.mcc.data) ?? [];
  const filteredMcc = mcc.filter((x) => !x.mcc.startsWith("3"));
  const isNab = process.env.IDP === "NAB";
  const mode = isNil(merchant) || isNil(merchant.id) ? CREATE : UPDATE;

  const stateOptions: Option[] = [
    { ACT: "Australian Capital Territory" },
    { NSW: "New South Wales" },
    { NT: "Northern Territory" },
    { QLD: "Queensland" },
    { SA: "South Australia" },
    { TAS: "Tasmania" },
    { VIC: "Victoria" },
    { WA: "Western Australia" },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <TextInput
          disabled={!isUpdateAllowed}
          label="Name*"
          fieldname="attributes.Name"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextInput
          disabled={!isUpdateAllowed}
          label="Address 1*"
          fieldname="attributes.Address1"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextInput
          disabled={!isUpdateAllowed}
          label="Address 2"
          fieldname="attributes.Address2"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextInput
          disabled={!isUpdateAllowed}
          label="Suburb*"
          fieldname="attributes.Suburb"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <SelectDropDown
          fieldname="attributes.State"
          label="State*"
          options={stateOptions}
          disabled={!isUpdateAllowed}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextInput
          disabled={!isUpdateAllowed}
          label="Postcode*"
          fieldname="attributes.Postcode"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        {filteredMcc.length && (
          <AutocompleteField
            fieldname="attributes.MCC"
            options={filteredMcc}
            value={mcc.find((c) => c.mcc === values.attributes.MCC || null)}
            onChange={(e, value: Mcc) =>
              !isNil(value) && setFieldValue("attributes.MCC", value?.mcc)
            }
            getOptionLabel={(option): string =>
              `${option.mcc} - ${option.combined_description}`
            }
            renderOption={(props, option: Mcc) => (
              <div
                key={option.mcc}
                {...props}
              >{`${option.mcc} - ${option.combined_description}`}</div>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="MCC*"
                fullWidth
                error={Boolean(
                  touched.attributes?.MCC && errors?.attributes?.MCC
                )}
                helperText={touched.attributes?.MCC && errors?.attributes?.MCC}
              />
            )}
            disabled={!isUpdateAllowed}
          />
        )}
      </Grid>
      <Grid item xs={12}>
        <TextInput
          minRows={4}
          multiline
          fieldname="attributes.Note"
          label="Note"
          disabled={!isUpdateAllowed}
        />
      </Grid>
    </Grid>
  );
};

export default memo(EditMerchantInformationComponent);
